import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence, type MotionProps, type Variants } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

interface Option {
  value: string;
  label: string;
}

interface AnimatedSelectProps {
  label: string;
  name: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  options: Option[];
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  loading?: boolean;
  className?: string;
}

const menu = {
  closed: {
    scale: 0.95,
    opacity: 0,
    y: -10,
    transition: {
      duration: 0.15,
      ease: "easeOut"
    },
  },
  open: {
    scale: 1,
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: "easeOut",
      delayChildren: 0.05,
      staggerChildren: 0.02,
    },
  },
} satisfies Variants;

const item = {
  variants: {
    closed: { x: -8, opacity: 0 },
    open: { x: 0, opacity: 1 },
  },
  transition: { 
    duration: 0.15,
    ease: "easeOut"
  },
} satisfies MotionProps;

export const AnimatedSelect: React.FC<AnimatedSelectProps> = ({
  label,
  name,
  value,
  onChange,
  options,
  required = false,
  disabled = false,
  placeholder = "Select an option",
  loading = false,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<Option | null>(
    options.find(opt => opt.value === value) || null
  );
  const selectRef = useRef<HTMLDivElement>(null);

  // Update selected option when value prop changes
  useEffect(() => {
    const option = options.find(opt => opt.value === value);
    setSelectedOption(option || null);
  }, [value, options]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionSelect = (option: Option) => {
    setSelectedOption(option);
    setIsOpen(false);
    
    // Create a synthetic event to maintain compatibility with existing form logic
    const syntheticEvent = {
      target: {
        name,
        value: option.value
      }
    } as React.ChangeEvent<HTMLSelectElement>;
    
    onChange(syntheticEvent);
  };

  const toggleDropdown = () => {
    if (!disabled && !loading) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <motion.div 
      className={`space-y-2 ${className} ${isOpen ? 'z-[10000]' : 'z-auto'}`}
      style={{ position: 'relative' }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {label && (
        <motion.label 
          className="block text-base font-medium text-night dark:text-white"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1, duration: 0.2 }}
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </motion.label>
      )}
      
      <div className="relative" ref={selectRef} style={{ overflow: 'visible', zIndex: isOpen ? 10001 : 'auto' }}>
        {/* Main Select Button */}
        <motion.button
          type="button"
          onClick={toggleDropdown}
          disabled={disabled || loading}
          whileHover={!disabled && !loading ? { scale: 1.005 } : {}}
          whileTap={!disabled && !loading ? { scale: 0.995 } : {}}
          className={`
            w-full px-3 py-2 text-left border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200
            ${disabled || loading 
              ? 'bg-gray-100 dark:bg-night-600 text-gray-500 dark:text-gray-400 cursor-not-allowed border-gray-300 dark:border-night-600' 
              : 'bg-white dark:bg-night-700 text-night dark:text-white border-gray-300 dark:border-night-600 hover:border-primary-400 dark:hover:border-primary-500'
            }
            ${isOpen ? 'ring-2 ring-primary-500 border-primary-500' : ''}
          `}
        >
          <div className="flex items-center justify-between">
            <span className={selectedOption ? 'text-night dark:text-white' : 'text-gray-500 dark:text-gray-400'}>
              {loading ? 'Loading...' : (selectedOption?.label || placeholder)}
            </span>
            
            <div className="flex items-center">
              {loading && (
                <svg className="animate-spin h-4 w-4 text-primary-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              
              <motion.div
                animate={{ rotate: isOpen ? 180 : 0 }}
                transition={{ duration: 0.2 }}
              >
                <ChevronDownIcon className="h-5 w-5 text-gray-400" />
              </motion.div>
            </div>
          </div>
        </motion.button>

        {/* Dropdown Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial="closed"
              animate="open"
              exit="closed"
              variants={menu}
              className="absolute w-full mt-1 bg-white dark:bg-night-700 border border-gray-300 dark:border-night-600 rounded-md shadow-xl max-h-60 overflow-auto"
              style={{ 
                transformOrigin: 'top',
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 10002
              }}
            >
              <div className="py-1">
                {options.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                    No options available
                  </div>
                ) : (
                  options.map((option) => (
                    <motion.button
                      key={option.value}
                      type="button"
                      {...item}
                      onClick={() => handleOptionSelect(option)}
                      className={`
                        w-full text-left px-3 py-2 text-sm transition-colors duration-150
                        ${selectedOption?.value === option.value
                          ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                          : 'text-night dark:text-white hover:bg-gray-50 dark:hover:bg-night-600'
                        }
                      `}
                    >
                      {option.label}
                    </motion.button>
                  ))
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}; 